import { memo } from 'react';
import { Checkbox, Tooltip } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ChevronDown, ChevronUp, Users, X } from 'lucide-react';
import {
  ASSIGNEE_TYPES,
  EDIT_PERMISSION_ORDER,
  PERMISSION_TYPES,
  VIEW_PERMISSION_ORDER,
  VIEW_PERMISSIONS,
} from '../constants';
import { asArray, userInitials } from '../utils/shareDashboardAssignmentUtils';
import DashboardButton from './DashboardButton';

const ShareAssignmentRow = memo(function ShareAssignmentRow({
  item,
  source = 'staged',
  isPermissionsOpen,
  onTogglePanel,
  editPermissionLabels,
  getAssignmentSummary,
  getAssignmentPermissionDetailLines,
  onToggleEditPermission,
  onToggleViewPermission,
  onToggleCreateMode,
  onRemove,
}) {
  const { t } = useTranslation('dashboards');

  return (
    <div
      data-permission-root="true"
      className="relative flex items-center justify-between gap-3 rounded-md border border-edge px-2 py-2"
    >
      <div className="flex min-w-0 items-center gap-2">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-teal-600 text-[10px] font-semibold text-white dark:bg-teal-500">
          {item.type === ASSIGNEE_TYPES.DEPARTMENT ? <Users size={10} /> : userInitials(item.name)}
        </span>
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-content">{item.name}</p>
          <p className="truncate text-[11px] text-content-muted">
            {item.type === ASSIGNEE_TYPES.DEPARTMENT
              ? t('common.members', { count: item.userCount || item.membersCount || 0 })
              : item.role || t('common.userFallbackRole')}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative inline-flex max-w-[min(220px,40vw)] shrink-0 flex-col items-stretch">
          <Tooltip
            position="top"
            maxWidth="280px"
            content={
              <ul className="m-0 list-disc space-y-1 py-0.5 pl-4 text-left">
                {getAssignmentPermissionDetailLines(item).map((line, idx) => (
                  <li key={`${item.id}-perm-${idx}`} className="text-sm leading-snug">
                    {line}
                  </li>
                ))}
              </ul>
            }
          >
            <button
              type="button"
              className="box-border inline-flex h-10 w-full min-w-0 cursor-pointer items-center justify-between gap-2 whitespace-nowrap rounded-full border border-[#5C5C5C] bg-surface px-4 py-2 text-xs font-medium text-content shadow-none hover:bg-surface-hover-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:border-gray-500"
              aria-expanded={isPermissionsOpen}
              aria-haspopup="listbox"
              onClick={onTogglePanel}
            >
              <span className="min-w-0 flex-1 truncate">{getAssignmentSummary(item)}</span>
              <ChevronDown size={16} strokeWidth={2} className="shrink-0 text-content-muted" aria-hidden />
            </button>
          </Tooltip>
          {isPermissionsOpen && (
            <div
              className="scrollbar-aimsos-auto absolute right-0 top-full z-20 mt-1.5 max-h-[300px] min-w-[300px] w-[min(340px,calc(100vw-2rem))] overflow-y-auto overflow-x-hidden rounded-lg border border-edge bg-surface shadow-lg"
              role="listbox"
              aria-label={t('share.modal.permissions.editSection')}
            >
              <div className="py-1">
                <div>
                  <div className="flex items-center justify-between px-4 pb-2 pt-4">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-content">
                      {t('share.modal.permissions.editSection')}
                    </span>
                    <ChevronUp size={14} strokeWidth={2} className="shrink-0 text-content-muted" aria-hidden />
                  </div>
                  <div className="pb-2">
                    {EDIT_PERMISSION_ORDER.map((permissionKey) => {
                      const editCheckboxId = `share-perm-${source}-${item.id}-edit-${permissionKey}`;
                      return (
                        <div
                          key={permissionKey}
                          className="flex items-center gap-3 px-4 py-3 text-xs font-medium text-content-secondary transition-colors hover:bg-surface-hover-subtle"
                        >
                          <Checkbox
                            id={editCheckboxId}
                            size="small"
                            checked={asArray(item.editPermissions).includes(permissionKey)}
                            onChange={() => onToggleEditPermission(permissionKey)}
                            aria-label={editPermissionLabels[permissionKey]}
                          />
                          <label htmlFor={editCheckboxId} className="min-w-0 flex-1 cursor-pointer leading-snug">
                            {editPermissionLabels[permissionKey]}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between px-4 pb-2 pt-4">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-content">
                      {t('share.modal.permissions.viewSection')}
                    </span>
                    <ChevronUp size={14} strokeWidth={2} className="shrink-0 text-content-muted" aria-hidden />
                  </div>
                  <div className="pb-2">
                    {VIEW_PERMISSION_ORDER.map((permissionKey) => {
                      const viewOnlyLocked = permissionKey === VIEW_PERMISSIONS.VIEW_ONLY;
                      const viewCheckboxId = `share-perm-${source}-${item.id}-view-${permissionKey}`;
                      const viewLabel =
                        permissionKey === VIEW_PERMISSIONS.VIEW_ONLY
                          ? t('share.modal.permissions.viewOnly')
                          : t('share.modal.permissions.viewExport');
                      return (
                        <div
                          key={permissionKey}
                          className="flex items-center gap-3 px-4 py-3 text-xs font-medium text-content-secondary transition-colors hover:bg-surface-hover-subtle"
                        >
                          <Checkbox
                            id={viewCheckboxId}
                            size="small"
                            checked={asArray(item.viewPermissions).includes(permissionKey)}
                            disabled={viewOnlyLocked}
                            onChange={() => onToggleViewPermission(permissionKey)}
                            aria-label={viewLabel}
                          />
                          {viewOnlyLocked ? (
                            <span className="min-w-0 flex-1 leading-snug opacity-90">{viewLabel}</span>
                          ) : (
                            <label htmlFor={viewCheckboxId} className="min-w-0 flex-1 cursor-pointer leading-snug">
                              {viewLabel}
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between px-4 pb-2 pt-4">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-content">
                      {t('share.modal.permissions.createSection')}
                    </span>
                    <ChevronUp size={14} strokeWidth={2} className="shrink-0 text-content-muted" aria-hidden />
                  </div>
                  <div className="flex items-center gap-3 px-4 py-3 pb-4 text-xs font-medium text-content-secondary transition-colors hover:bg-surface-hover-subtle">
                    <Checkbox
                      id={`share-perm-${source}-${item.id}-create`}
                      size="small"
                      checked={asArray(item.permissionModes).includes(PERMISSION_TYPES.CREATE)}
                      onChange={onToggleCreateMode}
                      aria-label={t('share.modal.permissions.createItem')}
                    />
                    <label
                      htmlFor={`share-perm-${source}-${item.id}-create`}
                      className="min-w-0 flex-1 cursor-pointer leading-snug"
                    >
                      {t('share.modal.permissions.createItem')}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <DashboardButton
          variant="ghost"
          size="sm"
          leftIcon={X}
          className="h-6 w-6 shrink-0 p-0 text-content-muted hover:text-content"
          onClick={onRemove}
          aria-label={t('share.modal.removeAssigneeAria')}
        />
      </div>
    </div>
  );
});

export default ShareAssignmentRow;
